<!-- IiccMain.vue -->
<template>
  <main class="iiccmain">
    <section class="hero" aria-labelledby="page-title">
      <div class="section__inner hero__inner">
        <h2 id="page-title">{{ pageTitle }}</h2>
        <p><strong>ICI Korea </strong> Recognizing Competency World-Wide</p>
      </div>
    </section>

    <section aria-label="About Navigation" class="about-tabs">
      <div class="section__inner">
        <nav class="tabs" role="tablist" aria-label="상위 탭">
          <button
            type="button"
            :class="{ active: isSelf }"
            @click="goSelf"
            role="tab"
            :aria-selected="isSelf ? 'true' : 'false'"
          >
            지배구조 진단
          </button>

          <button
            type="button"
            :class="{ active: isIicc }"
            @click="goIicc"
            role="tab"
            :aria-selected="isIicc ? 'true' : 'false'"
          >
            내부통제 진단
          </button>

          <button
            type="button"
            :class="{ active: isCert }"
            @click="goCert"
            role="tab"
            :aria-selected="isCert ? 'true' : 'false'"
          >
            내부통제 국제인증
          </button>
        </nav>
      </div>
    </section>

    <section class="section__inner content">
      <KeepAlive>
        <component :is="currentComponent" />
      </KeepAlive>
    </section>
  </main>
</template>

<script setup>
import { computed, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import SelfAssessment from '@/pages/iicc/selfassessment/SelfAssessment.vue'
import Iicc from '@/pages/iicc/iicc-page/Iicc.vue'
import IiccCertification from '@/pages/iicc/iicc-certification/IiccCertification.vue'

const router = useRouter()
const route = useRoute()

const allowedTabs = ['selfassessment', 'iicc', 'certification']

const activeTab = computed(() => {
  const tab = route.query.tab
  return allowedTabs.includes(tab) ? tab : 'iicc'
})

const isSelf = computed(() => activeTab.value === 'selfassessment')
const isIicc = computed(() => activeTab.value === 'iicc')
const isCert = computed(() => activeTab.value === 'certification')

const pageTitle = computed(() => {
  if (isSelf.value) return 'ESG Governance 진단'
  if (isIicc.value) return '내부통제 진단'
  if (isCert.value) return '내부통제 국제인증'
  return '내부통제 국제인증'
})

const currentComponent = computed(() => {
  switch (activeTab.value) {
    case 'selfassessment':
      return SelfAssessment
    case 'certification':
      return IiccCertification
    case 'iicc':
    default:
      return Iicc
  }
})

const goSelf = () => {
  router.push({
    path: '/iiccmain',
    query: {
      ...route.query,
      tab: 'selfassessment',
      subTab: route.query.subTab || 'surveypublic',
    },
  })
}

const goIicc = () => {
  const nextQuery = { ...route.query, tab: 'iicc' }
  delete nextQuery.subTab

  router.push({
    path: '/iiccmain',
    query: nextQuery,
  })
}

const goCert = () => {
  const nextQuery = { ...route.query, tab: 'certification' }
  delete nextQuery.subTab

  router.push({
    path: '/iiccmain',
    query: nextQuery,
  })
}

watchEffect(() => {
  if (!allowedTabs.includes(route.query.tab)) {
    router.replace({
      path: '/iiccmain',
      query: {
        ...route.query,
        tab: 'iicc',
      },
    })
  }
})
</script>

<style scoped>
.iiccmain {
  display: grid;
  grid-template-rows: auto auto 1fr;
  font-family: 'Noto Sans KR', sans-serif;
}

.section__inner {
  max-width: 1200px;
  margin-inline: auto;
  padding: 20px 16px;
}

.content {
  display: grid;
}

.hero {
  background:
    linear-gradient(rgba(13, 6, 20, .65), rgba(13, 6, 20, .65)),
    url('/src/assets/iicc-main.jpeg') center 70% / 65% no-repeat;
  color: #fff;
}

.hero__inner {
  padding: 130px 16px;
  text-align: center;
}

.hero h2 {
  font-family: 'Noto Sans KR', sans-serif;
  font-weight: 700;
  color: #fff;
  font-size: 2.5rem;
  text-align: center;
  letter-spacing: -1px;
  line-height: 1.3;
}

.hero p {
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, .9);
  text-align: center;
  margin-top: 12px;
  letter-spacing: -.3px;
  line-height: 1.6;
}

.hero p strong {
  font-size: 1.225rem;
  font-weight: 700;
}

.tabs {
  display: grid;
  grid-auto-flow: column;
  justify-content: center;
  gap: 1rem;
  border-bottom: 2px solid #e5e5e5;
  padding: .5rem 0;
}

.tabs button {
  padding: .75rem 1.5rem;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #444;
  position: relative;
  font-size: 1.1rem;
  font-family: 'Noto Sans KR', sans-serif;
}

.tabs button.active {
  color: var(--main-color);
  font-weight: 600;
}

.tabs button.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 3px;
  background: var(--main-color);
}

@media (max-width: 768px) {
  .tabs {
    grid-auto-flow: row;
    justify-content: stretch;
  }

  .tabs button {
    width: 100%;
    border-bottom: 1px solid #e5e5e5;
  }

  .tabs button.active::after {
    bottom: 0;
  }
}
</style>